/**
 * ============================================================================
 * GRATIA - assets/recommendations.js
 * ----------------------------------------------------------------------------
 * <product-recommendations> - lazy-loads the Product Recommendations API
 *   section HTML when scrolled into view.
 * <recently-viewed>         - builds a Search-API query from localStorage
 *   history and injects the rendered product cards.
 * Both replace their own innerHTML with the fetched section's inner markup.
 * ============================================================================
 */
(function () {
  'use strict';

  function swapInner(el, html, selector) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(selector);
    if (fresh) {
      el.innerHTML = fresh.innerHTML;
      el.hidden = false;
      window.Gratia?.initAccordions?.(el);
      // re-trigger reveal observer
      document.dispatchEvent(new CustomEvent('gratia:content-loaded', { detail: { root: el } }));
    } else {
      el.remove();
    }
  }

  function whenVisible(el, cb) {
    const io = new IntersectionObserver((entries, obs) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { obs.disconnect(); cb(); }
      });
    }, { rootMargin: '300px' });
    io.observe(el);
  }

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      whenVisible(this, () => this.load(url));
    }
    async load(url) {
      try {
        const res = await fetch(url);
        const text = await res.text();
        swapInner(this, text, 'product-recommendations');
      } catch { this.remove(); }
    }
  }
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      let handles = [];
      try { handles = JSON.parse(localStorage.getItem('gratia:recently-viewed') || '[]'); } catch { handles = []; }
      const exclude = this.dataset.exclude;
      if (exclude) handles = handles.filter((h) => h !== exclude);
      if (!handles.length) { this.remove(); return; }

      const limit = parseInt(this.dataset.limit, 10) || 4;
      const query = handles.slice(0, limit + 2).map((h) => `handle:${h}`).join(' OR ');
      const url = `${this.dataset.url}&q=${encodeURIComponent(query)}`;
      whenVisible(this, () => this.load(url));
    }
    async load(url) {
      try {
        const res = await fetch(url);
        const text = await res.text();
        swapInner(this, text, 'recently-viewed');
      } catch { this.remove(); }
    }
  }
  if (!customElements.get('recently-viewed')) {
    customElements.define('recently-viewed', RecentlyViewed);
  }
})();
